import React from 'react';
import { SETTING, ControlType, SelectionType } from 'interfaces/settings';
import { Typography } from 'interfaces/typography';
import { useAutodialerSettings } from '../hooks/useAutodialerSettings';
import { NotSetSetting } from './NotSetSetting';
import { Tooltip } from 'utils/CommonUIComponents';
import { SETTING_TOOLTIPS } from '../config/settingTooltips';
import { SETTING_TO_CONTROL_TYPE, SETTING_TO_DISPLAY_NAME } from '../config/settingMaps';
import { HelpCircle } from 'lucide-react';
import { RangeSettingControl } from '../SettingsTypes/range/RangeSettingControl';
import { BooleanSettingControl } from '../SettingsTypes/boolean/BooleanSettingControl';
import { SelectSettingControl } from '../SettingsTypes/categorical/SelectSettingControl';
import { LockedSettingDisplay } from './LockedSettingDisplay';
import { DifferentUserSettingDisplay } from './DifferentUserSettingDisplay';
import { SettingOption, SettingOptionType } from './SettingSection';
import { CustomSettingControl } from '../SettingsTypes/custom/CustomSettingControl';
import { LoggingMappingsControl } from '../SettingsTypes/logging/LoggingMappingsControl';

interface SettingRowProps {
    option: SettingOption;
}

function SingleSettingControl({ setting, isInMultiRow }: { setting: SETTING; isInMultiRow: boolean }) {
    const { getValueForSetting, getSettingIsEditable, getSettingSetByDifferentSelection } = useAutodialerSettings();
    
    const controlType = SETTING_TO_CONTROL_TYPE[setting];
    const value = getValueForSetting(setting)
    const canBeChanged = getSettingIsEditable(setting)
    const isSetByDifferent = getSettingSetByDifferentSelection(setting)
    const isSet = value !== null

    const renderSettingControl = () => {
        if (!canBeChanged) return <LockedSettingDisplay setting={setting} isInMultiRow={isInMultiRow} />;
        if (!isSet) return <NotSetSetting setting={setting} isInMultiRow={isInMultiRow} />;
        if (isSetByDifferent) return <DifferentUserSettingDisplay setting={setting} isInMultiRow={isInMultiRow} />;
        
        switch (controlType) {
            case ControlType.RANGE: return <RangeSettingControl setting={setting} />;
            case ControlType.TOGGLE: return <BooleanSettingControl setting={setting} />;
            case ControlType.SELECT: return <SelectSettingControl setting={setting} />;
            default: return null;
        }
    };

    // Determine flex behavior based on control type and state
    const isRangeControl = controlType === ControlType.RANGE;
    
    // Range controls, custom controls, and unset/default settings should flex to fill space
    // Toggle and select controls should be fixed size unless they're defaults
    const shouldFlexGrow = isRangeControl || !isSet || isSetByDifferent || !canBeChanged;
    const flexClass = shouldFlexGrow ? 'flex-1' : 'flex-shrink-0';
    
    return (
        <div className={`${flexClass} min-h-0`}>
            {renderSettingControl()}
        </div>
    )
}

function UnsetButton({ settings }: { settings: SETTING[] }) {
    const { updateSetting } = useAutodialerSettings();
    
    const onUnset = () => {
        settings.forEach(setting => {
            updateSetting(setting, null);
        });
    };
    
    return (
        <button
            onClick={onUnset}
            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-all duration-200 group flex-shrink-0"
            title="Unset this setting"
        >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="group-hover:scale-110 transition-transform">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
        </button>
    );
}

function OverrideButton({ settings }: { settings: SETTING[] }) {
    const { updateSetting, getDefaultValueForSetting } = useAutodialerSettings();
    const onOverride = () => {
        settings.forEach(setting => {
            const defaultValue = getDefaultValueForSetting(setting);
            updateSetting(setting, defaultValue)
        })
    }
    
    return (
        <button
            onClick={onOverride}
            className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-all duration-200 flex-shrink-0 shadow-sm hover:shadow-md"
            title="Override this setting"
        >
            <Typography variant="smallParagraph" color="white" fontWeight="500">
                Override
            </Typography>
        </button>
    );
}

export function CustomSettingRow({ settings }: { settings: SETTING[] }) {
    const { getValueForSetting, getSettingIsEditable, getSettingSetByDifferentSelection } = useAutodialerSettings()
    const values = settings.map(setting => getValueForSetting(setting))
    const canBeChanged = settings.every(setting => getSettingIsEditable(setting))
    const isSetByDifferent = settings.some(setting => getSettingSetByDifferentSelection(setting))
    const isSet = values.some(value => value !== null)
    if (!canBeChanged) return <LockedSettingDisplay setting={settings[0]} isInMultiRow={false} />
    if (!isSet) return <NotSetSetting setting={settings[0]} isInMultiRow={false} />
    if (isSetByDifferent) return <DifferentUserSettingDisplay setting={settings[0]} isInMultiRow={true} />
    return (
        <div className={'flex-1 min-h-0'}> <CustomSettingControl settings={settings} /></div>
    )
}

export function SettingRow({ option }: SettingRowProps) {
    const { getValueForSetting, getSettingIsEditable, getSettingSetByDifferentSelection } = useAutodialerSettings();
    
    if (option.type === SettingOptionType.LOGGING) {
        return (
            <div className="py-4">
                <LoggingMappingsControl />
            </div>
        );
    }
    
    const displayName = option.type === SettingOptionType.SINGLE ? SETTING_TO_DISPLAY_NAME[option.setting] : option.rowLabel;
    const settings = option.type === SettingOptionType.MULTI ? option.settings : [option.setting]
    const tooltipContent = settings.map(setting => SETTING_TOOLTIPS[setting]).filter(Boolean).join('\n')
    const hasCustomSetting = settings.some(setting => SETTING_TO_CONTROL_TYPE[setting] === ControlType.CUSTOM)
    const isPhonePreference = settings.includes(SETTING.PHONE_PREFERENCE);
    const hasSetValues = settings.some(setting => {
        const value = getValueForSetting(setting);
        return value !== null;
    })
    const canBeChanged = settings.some(setting => getSettingIsEditable(setting));
    const isSetByDifferent = settings.some(setting => getSettingSetByDifferentSelection(setting));

    return (
        <div className={`flex gap-4 py-0.5 min-h-0 ${isPhonePreference ? 'items-start' : 'items-center'}`}>
            {/* Left side: Setting name and help icon */}
            <div className="flex items-center justify-between w-48 flex-shrink-0">
                <Typography 
                    variant="largeParagraph" 
                    fontWeight="500"
                    color="#374151"
                >
                    {displayName}
                </Typography>
                {tooltipContent && (
                    <Tooltip content={tooltipContent} placement="top">
                        <HelpCircle 
                            size={16} 
                            className="cursor-help text-gray-400"
                        />
                    </Tooltip>
                )}
            </div>

            {/* Right side: Setting controls */}
            <div className="flex-1 flex items-center gap-2 min-h-0">
                {hasCustomSetting ? 
                <CustomSettingRow settings={settings} /> : 
                settings.map((setting) => (
                    <SingleSettingControl
                        key={setting}
                        setting={setting}
                        isInMultiRow={option.type === SettingOptionType.MULTI}
                    />
                ))}
            </div>
            {isSetByDifferent && canBeChanged ? (
                <OverrideButton settings={settings} />
            ) : hasSetValues && canBeChanged ? (
                <UnsetButton settings={settings} />
            ) : null}
        </div>
    );
} 